/**
 * iter160j — Schäden-Tab im Mietverhältnis-Detail.
 * Zeigt alle Schäden zu diesem MV + Button „Neuer Schaden" mit vorbelegtem MV.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/constants";
import { Warning, Plus } from "@phosphor-icons/react";
import { CreateDamageDialog } from "@/pages/Schaeden";

const DAMAGE_STATUS = {
  reported: { label: "Gemeldet", cls: "bg-amber-100 text-amber-800" },
  in_progress: { label: "In Bearbeitung", cls: "bg-blue-100 text-blue-800" },
  contractor_assigned: { label: "Handwerker beauftragt", cls: "bg-indigo-100 text-indigo-800" },
  fixed: { label: "Behoben", cls: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Abgelehnt", cls: "bg-slate-200 text-slate-700" },
  closed: { label: "Abgeschlossen", cls: "bg-slate-100 text-slate-600" },
};

export function TenancyDamagesTab({ tenancyId, propertyId, unitId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/damages", { params: { tenancy_id: tenancyId } });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Schäden konnten nicht geladen werden");
    }
  };
  useEffect(() => { load(); }, [tenancyId]);

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">
          {items.length === 0 ? "Keine Schäden für dieses Mietverhältnis erfasst." : `${items.length} Schaden${items.length > 1 ? "sfälle" : ""} für dieses MV`}
        </div>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="new-damage-from-tenancy">
          <Plus size={14} className="mr-1" /> Schaden erfassen
        </Button>
      </div>

      {items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Schaden-Nr.</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Titel</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Priorität</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Status</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">Datum</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => {
                const s = DAMAGE_STATUS[d.status] || DAMAGE_STATUS.reported;
                return (
                  <tr key={d.id}
                      className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/schaeden`)}
                      data-testid={`tenancy-damage-${d.id}`}>
                    <td className="px-3 py-2 font-medium tabular-nums text-slate-900">{d.damage_number}</td>
                    <td className="px-3 py-2 text-slate-800">{d.title}</td>
                    <td className="px-3 py-2 text-xs">{d.priority}</td>
                    <td className="px-3 py-2"><Badge className={s.cls}>{s.label}</Badge></td>
                    <td className="px-3 py-2 text-xs text-slate-600 tabular-nums">{formatDate(d.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreateDamageDialog
        open={open}
        onClose={() => setOpen(false)}
        onCreated={load}
        prefill={{ property_id: propertyId, unit_id: unitId, tenancy_id: tenancyId }}
      />
    </div>
  );
}
